import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { validateLogin } from "../utils/loginValidation";
import { useAuth } from "../../../app/context/AuthContext";
import { getUserErrorMessage, logApiError } from "../../../core/utils/apiErrorUtils";
import { SPLASH_ANIMATION_MS } from "../../../ui/components/SplashLoader";

function waitForSplashCycle(startedAt, durationMs = SPLASH_ANIMATION_MS) {
  const elapsed = Date.now() - startedAt;

  // Always finish at least one full playthrough.
  if (elapsed < durationMs) {
    return new Promise((resolve) =>
      setTimeout(resolve, durationMs - elapsed),
    );
  }

  // If login ran longer, wait until the current loop ends so we don't cut mid-frame.
  const intoCycle = elapsed % durationMs;
  if (intoCycle === 0) return Promise.resolve();
  return new Promise((resolve) =>
    setTimeout(resolve, durationMs - intoCycle),
  );
}

export function useLogin() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading: authLoading } = useAuth();

  const from = location.state?.from?.pathname || "/";

  const handleLogin = async ({ email, password }) => {
    setError("");
    const validationError = validateLogin(email, password);
    if (validationError) {
      setError(validationError);
      return;
    }

    const splashStartedAt = Date.now();

    try {
      setLoading(true);
      const result = await login(email, password);

      await waitForSplashCycle(splashStartedAt);

      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (error) {
      await waitForSplashCycle(splashStartedAt);
      logApiError("Login submit", error);
      setError(getUserErrorMessage(error, "Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return {
    handleLogin,
    error,
    loading: loading || authLoading,
  };
}
